import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    Box,
    Button,
    Grid,
    TextField,
    MenuItem,
    Typography,
    Paper,
    Divider,
    Stack,
    CircularProgress,
} from '@mui/material';
import { LeadCreate, LeadUpdate, LeadStatus, LeadSource } from '../../types/lead';

// Validation Schema matching backend rules
const leadSchema = yup.object().shape({
    name: yup
        .string()
        .required('Name is required')
        .min(1, 'Name must be at least 1 character')
        .max(255, 'Name must be at most 255 characters'),
    email: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .email('Invalid email format')
        .max(255, 'Email must be at most 255 characters'),
    phone: yup
        .string()
        .required('Phone number is required')
        .matches(/^[6-9]\d{9}$/, 'Phone number must be exactly 10 digits and start with 6, 7, 8, or 9'),
    company: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .max(255, 'Company name must be at most 255 characters'),
    gst_number: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .matches(
            /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/,
            {
                message: 'Invalid GST number format (e.g., 22AAAAA0000A1Z5)',
                excludeEmptyString: true,
            }
        ),
    location: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .max(255, 'Location must be at most 255 characters'),
    city: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .max(100, 'City must be at most 100 characters'),
    state: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .max(100, 'State must be at most 100 characters'),
    source: yup
        .mixed<LeadSource>()
        .oneOf(Object.values(LeadSource))
        .required('Source is required'),
    lead_type: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value)),
    status: yup
        .mixed<LeadStatus>()
        .oneOf(Object.values(LeadStatus))
        .default(LeadStatus.NEW),
    interest_category: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .max(100, 'Interest category must be at most 100 characters'),
    notes: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .max(2000, 'Notes must be at most 2000 characters'),
});

interface LeadFormProps {
    initialData?: LeadUpdate;
    onSubmit: (data: LeadCreate) => void;
    isLoading?: boolean;
    isEdit?: boolean;
}

const LeadForm: React.FC<LeadFormProps> = ({
    initialData,
    onSubmit,
    isLoading = false,
    isEdit = false,
}) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<any>({
        resolver: yupResolver(leadSchema),
        defaultValues: {
            name: initialData?.name || '',
            email: initialData?.email || '',
            phone: initialData?.phone || '',
            company: initialData?.company || '',
            gst_number: initialData?.gst_number || '',
            location: initialData?.location || '',
            city: initialData?.city || '',
            state: initialData?.state || '',
            source: initialData?.source || LeadSource.MANUAL_ENTRY,
            lead_type: initialData?.lead_type || 'wholesaler',
            status: initialData?.status || LeadStatus.NEW,
            interest_category: initialData?.interest_category || '',
            notes: initialData?.notes || '',
            campaign_tags: initialData?.campaign_tags || [],
        },
    });

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={3}>
                {/* Basic Information Section */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Basic Information
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="name"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Lead Name"
                                            required
                                            error={!!errors.name}
                                            helperText={errors.name?.message as string}
                                            placeholder="e.g. John Doe"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="phone"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Phone Number"
                                            required
                                            error={!!errors.phone}
                                            helperText={errors.phone?.message as string}
                                            placeholder="e.g. 9876543210"
                                            inputProps={{ maxLength: 10 }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Email Address"
                                            error={!!errors.email}
                                            helperText={errors.email?.message as string}
                                            placeholder="e.g. john@example.com"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="company"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Company Name"
                                            error={!!errors.company}
                                            helperText={errors.company?.message as string}
                                            placeholder="e.g. Acme Corp"
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Status & Source Section */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Status & Source
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            select
                                            fullWidth
                                            label="Lead Status"
                                            error={!!errors.status}
                                            helperText={errors.status?.message as string}
                                        >
                                            {Object.values(LeadStatus).map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option.charAt(0).toUpperCase() + option.slice(1)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="source"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            select
                                            fullWidth
                                            label="Lead Source"
                                            required
                                            error={!!errors.source}
                                            helperText={errors.source?.message as string}
                                        >
                                            {Object.values(LeadSource).map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option.charAt(0).toUpperCase() + option.slice(1).replace('_', ' ')}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="lead_type"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            select
                                            fullWidth
                                            label="Buyer / Lead Type"
                                        >
                                            <MenuItem value="wholesaler">Wholesaler</MenuItem>
                                            <MenuItem value="distributor">Distributor</MenuItem>
                                            <MenuItem value="retailer">Retailer</MenuItem>
                                            <MenuItem value="epc_contractor">EPC Contractor</MenuItem>
                                            <MenuItem value="corporate">Corporate Buyer</MenuItem>
                                            <MenuItem value="individual">Individual</MenuItem>
                                            <MenuItem value="other">Other</MenuItem>
                                        </TextField>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="interest_category"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Interest Category"
                                            error={!!errors.interest_category}
                                            helperText={errors.interest_category?.message as string}
                                            placeholder="e.g. Textile, Apparel"
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Location & Tax Section */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Location & Tax
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Controller
                                    name="gst_number"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="GST Number"
                                            error={!!errors.gst_number}
                                            helperText={errors.gst_number?.message as string}
                                            placeholder="e.g. 22AAAAA0000A1Z5"
                                            inputProps={{ style: { textTransform: 'uppercase' } }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="city"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="City"
                                            error={!!errors.city}
                                            helperText={errors.city?.message as string}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="state"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="State"
                                            error={!!errors.state}
                                            helperText={errors.state?.message as string}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="location"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Location Details"
                                            multiline
                                            rows={1}
                                            error={!!errors.location}
                                            helperText={errors.location?.message as string}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Notes Section */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Additional Notes
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Controller
                            name="notes"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label="Notes"
                                    multiline
                                    rows={4}
                                    error={!!errors.notes}
                                    helperText={errors.notes?.message as string}
                                    placeholder="Enter any additional details about the lead..."
                                />
                            )}
                        />
                    </Paper>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={() => window.history.back()}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} /> : null}
                        >
                            {isLoading ? 'Saving...' : isEdit ? 'Update Lead' : 'Create Lead'}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

export default LeadForm;
